import { FileAddOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Col, Form, Input, message, Row } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PLAYER_COACH } from '../../../../constants/define/translation';
import { appStyles, defaultImage, formScrolling, selectSelector } from '../../../../styles';
import { CONFIG_CONTAINER_NAME, uploadFileToBlob, UploadFileToBlobInterface } from '../../../../utils/azure';
import { ButtonActionPage, SpinLoading, TextAreaQuill } from '../../../atoms';
import { DragDropImage } from '../../../molecules/upload';
import { PreviewCoachProfile } from '../../../molecules/preview/common/PreviewCoachProfile';
import { CoachProfileStatus, CoachProfileType } from '../../../../types/coachProfile';
import {
  patchUpdateCoachProfileRepository,
  postCreateCoachProfileRepository,
} from '../../../../repository/mytm/coach-profile/coachProfileRepository';

interface FormCoachProfileProps {
  record?: CoachProfileType;
  onRefresh: () => void;
  onCloseModal: () => void;
  setDataRecord: Function;
  countrySelected: string;
}

const azureCDN = process.env.REACT_APP_AZURE_CDN;
const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;

export const FormCoachProfile = (props: FormCoachProfileProps) => {
  const { t } = useTranslation(PLAYER_COACH);
  const { record, onRefresh, onCloseModal, setDataRecord, countrySelected } = props;
  const [form] = Form.useForm();
  const options = record?.options ? JSON.parse(record.options) : null;
  const defaultValue = {
    title: record?.title,
    imageLink: record?.imageLink,
    country: record ? record?.countries?.split(',') : [countrySelected] || 'USA',
    tag: record?.tags,
    description: record?.description,
    drillTitle: options?.drillTitle || '',
  };
  const [isCreateLoading, setCreateLoading] = useState(false);
  const [imageList, setImageList] = useState<UploadFileToBlobInterface[]>([]);
  const [messageErrorUploadImage, setMessageErrorUploadImage] = useState<string>();
  const [selectedImage, setSelectedImage] = useState<number>(0);
  const [dataForm, setDataForm] = useState<any | undefined>(defaultValue);

  const onSelectedImage = useCallback((index: number) => {
    setSelectedImage(index);
  }, []);

  const onChangeInput = useCallback(
    e => {
      const newField = form.getFieldsValue();
      setDataForm((old: any) => ({
        ...old,
        title: newField?.title,
        tag: newField?.tag,
        drillTitle: newField?.drillTitle,
        description: newField?.description || '',
      }));
    },
    [form],
  );

  const onSetImageList = useCallback((data: UploadFileToBlobInterface[]) => {
    setImageList(data);
  }, []);

  const onFinish = useCallback(
    (formValue: any) => {
      if (!imageList.length) {
        setMessageErrorUploadImage(t('validate.imageBlank'));
        return;
      }
      setCreateLoading(true);
      const imgList = imageList.filter(img => !img.isImageURL);
      const imgLink = imageList.filter(img => img.isImageURL);
      uploadFileToBlob(imgList, CONFIG_CONTAINER_NAME.UPLOADS)
        .then((images: any[]) => {
          let newImagesCDN = images;
          if (azureCDN) {
            newImagesCDN = images.map(url => url.replace(`${storageAccountName}.blob.core.windows.net`, azureCDN));
          }
          const url = newImagesCDN && newImagesCDN.length ? newImagesCDN[0] : imgLink.map(v => v.dataURL)[0];
          const newOptions = {
            drillTitle: dataForm?.drillTitle,
          };
          const payloadToPost = {
            title: dataForm?.title,
            imageLink: url || '',
            countries: 'USA',
            tags: dataForm?.tag,
            status: CoachProfileStatus.ACTIVE,
            description: dataForm?.description,
            options: newOptions,
          };
          if (record) {
            return patchUpdateCoachProfileRepository({
              ...payloadToPost,
              id: record?.id,
            })
              .then(() => {
                onRefresh();
                onCloseModal();
                message.success(t('message.success.updateCoachProfile'));
              })
              .catch(e => {
                setCreateLoading(true);
                message.error(e?.errorMessage);
              })
              .finally(() => {
                setCreateLoading(false);
              });
          }
          return postCreateCoachProfileRepository(payloadToPost)
            .then(() => {
              onRefresh();
              onCloseModal();
              message.success(t('message.success.createCoachProfile'));
            })
            .catch(e => {
              setCreateLoading(true);
              message.error(e?.errorMessage);
            })
            .finally(() => {
              setCreateLoading(false);
              setDataRecord(null);
            });
        })
        .catch(() => {
          setCreateLoading(true);
          message.error(t('message.error.uploadImage'));
        })
        .finally(() => {
          setCreateLoading(false);
        });
    },
    [record, imageList, dataForm],
  );

  useEffect(() => {
    if (record) {
      const imgList: UploadFileToBlobInterface[] = [{ dataURL: record?.imageLink, isImageURL: true }];
      setImageList(imgList);
      setSelectedImage(0);
      setDataForm({
        ...defaultValue,
        country: record?.countries?.split(','),
      });
    }
  }, [record]);

  useEffect(() => {
    if (imageList.length) {
      setMessageErrorUploadImage('');
    }
  }, [imageList]);

  return (
    <>
      <SpinLoading isLoading={isCreateLoading} absolute>
        <SwitchContainer>
          <Form form={form} layout="vertical" initialValues={defaultValue} onFinish={onFinish} name="form-CoachProfile">
            <Row className="form-CoachProfile-container" gutter={[24, 24]}>
              <StyledSelectImageCol span={9}>
                <DragDropImage
                  {...{
                    imageList,
                    onSetImageList,
                    messageErrorUploadImage,
                    selectedImage,
                    onSelectedImage,
                    maxImage: 1,
                    nodePreview: (
                      <PreviewCoachProfile
                        values={{
                          ...dataForm,
                          backgroundImage: imageList?.length && imageList?.find(item => !item.isLogo)?.dataURL,
                          describeText: t('form.imageUpload'),
                          describeText2: t('form.image3x4'),
                        }}
                        className="preview-coach-profile"
                        height="186px"
                      />
                    ),
                  }}
                />
              </StyledSelectImageCol>
              <StyledFormCoachProfile span={15}>
                <div className="form-scroll">
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="title"
                        label={t('form.title')}
                        rules={[{ required: true, message: t('validate.titleBlank') }]}
                      >
                        <Input placeholder={t('form.title')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="tag"
                        label={t('form.tag')}
                        rules={[{ required: true, message: t('validate.tagBlank') }]}
                      >
                        <Input placeholder={t('form.tag')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item name="description" label={t('form.description')} initialValue="">
                        <TextAreaQuill
                          theme="snow"
                          bounds=".quill"
                          placeholder=""
                          value={dataForm.description}
                          onChange={onChangeInput}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="drillTitle"
                        label={t('form.drillTitle')}
                        rules={[{ required: true, message: t('validate.drillTitleBlank') }]}
                      >
                        <Input placeholder={t('form.drillTitle')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row style={{ justifyContent: 'flex-end' }}>
                    <BtnContainer>
                      <ButtonActionPage
                        loading={isCreateLoading}
                        text={record ? t('form.btnUpdateCoachProfile') : t('form.btnCreateCoachProfile')}
                        id="1"
                        className="btn-submit-form-home"
                        htmlType="submit"
                        icon={!record ? <FileAddOutlined /> : undefined}
                      />
                    </BtnContainer>
                  </Row>
                </div>
              </StyledFormCoachProfile>
            </Row>
          </Form>
        </SwitchContainer>
      </SpinLoading>
    </>
  );
};

const SwitchContainer = styled.div`
  & .ant-form-item-label {
    font-weight: bold;
    font-size: 15px;
  }

  & .form-CoachProfile-container {
    padding: 1.5rem;

    .form-CoachProfile {
      resize: none;
    }
  }

  & .preview-coach-profile {
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
    border: 1px #787878 solid;
  }
`;

const BtnContainer = styled.div`
  & .btn-submit-form-home {
    width: 160px;
    bottom: -15px !important;
  }
`;

const StyledSelectImageCol = styled(Col)`
  padding: 0px !important;
  min-height: 400px;
  height: 520px;
  background-color: #efefef;
  position: relative;
  ${defaultImage}
`;

const StyledFormCoachProfile = styled(Col)`
  ${formScrolling}
  ${selectSelector}

  & input[type='text'],
  & input[type='number'] {
    ${appStyles.inputStyle}
  }

  & .add-partner {
    float: right;
    color: #3490ff;
    font-weight: bold;
    font-size: 14px;
    position: absolute;
    right: 0;
    z-index: 9999;
    background-color: transparent;
    border: none;
    cursor: pointer;
    text-decoration: underline;
    display: inline;
  }

  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & button.btn-submit-form-home {
    ${appStyles.commonButtonSubmitted}
  }

  & .ant-form-item {
    margin-bottom: 0.5rem;
  }

  & .ant-picker {
    width: 100%;
    padding: 0;
    ${appStyles.commonBorderRadius}
    & input {
      padding: 4px 0 4px 12px;
    }
    & .ant-picker-suffix {
      font-size: 20px;
      ${appStyles.background.black}
      ${appStyles.color.white}
      padding: 8px;
    }
  }

  & textarea {
    ${appStyles.commonBorderRadius}
  }

  & .ant-input-affix-wrapper {
    padding: 0 11px;
    ${appStyles.commonBorderRadius}
  }

  & .ant-form-item {
    ${appStyles.commonItemMarginBottom}
  }

  & .ant-form-item-explain {
    margin: 0;
  }

  & .ant-picker-clear {
    right: 40px;
  }

  & .ant-descriptions {
    cursor: default;
    & .ant-descriptions-header {
      margin-bottom: 5px;
    }
  }

  & .ant-upload-span {
    display: none !important;
  }

  & .popover {
    position: absolute;
    z-index: 2;
    bottom: 0;
    left: 10%;
  }

  & .popoverSub {
    position: absolute;
    z-index: 2;
    bottom: 0;
    right: 0;
  }

  & .cover {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  & .sketch-picker {
    cursor: pointer;
  }
`;
